import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbDropdown, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { NgxMaskDirective } from 'ngx-mask';
import { RouterLink } from '@angular/router';
import {
  BrandModel, ClientFullModel, ClientPropertyModel, CoinTransactionModel, CommentModel, ContactModel, CountryModel,
  HxBrandService, HxClientService, HxCoinService, HxCountryService, HxUiLabelPipe, SeekDirection
} from 'hx-services';
import { HxToastrService } from '../../services/toastr.service';

@Component({
  selector: 'hx-client-profile-modal',
  templateUrl: './client-profile.modal.html',
  styleUrls: ['./client-profile.modal.css'],
  encapsulation: ViewEncapsulation.None,
  imports: [FormsModule, NgIf, NgFor, DatePipe, TranslocoPipe, HxUiLabelPipe, NgbDropdown, NgbDropdownMenu, NgbDropdownToggle,
    NgxMaskDirective, RouterLink]
})
export class HxClientProfileModal implements OnInit {
  @Input() id!: number;
  @Input() brandId = 1;
  @Input() countryId = 1;
  client?: ClientFullModel;
  countries: CountryModel[] = [];
  hasStoredPhone = false;
  originalBirthDate?: string;
  contacts: ContactModel[] = [];
  comment = '';
  tab: 'profile' | 'brand' | 'coins' | 'comments' = 'profile';
  brands: BrandModel[] = [];
  properties: ClientPropertyModel[] = [];
  propertyDraft?: {id?: number; brandId: number; vip: boolean; blacklisted: boolean; note: string};
  coins: CoinTransactionModel[] = [];
  activeCoins = 0;
  coinBrandId = 1;
  coinCountryId = 1;
  coinSeek?: {first?: string; last?: string};
  coinPage = 1;
  coinHasNext = false;
  phonePatterns = {
    X: {pattern: /[04567]/},
    '9': {pattern: /9/},
    N: {pattern: /[0-24-9]/},
    '0': {pattern: /\d/}
  };
  calendarOpen = false;
  calendarYear = 2000;
  calendarMonth = 1;
  comments: CommentModel[] = [];
  loading = false;
  saving = false;
  sectionLoading = false;
  error = false;
  private sectionRequestId = 0;

  constructor(
    public activeModal: NgbActiveModal,
    private clients: HxClientService,
    private countriesService: HxCountryService,
    private brandsService: HxBrandService,
    private coinService: HxCoinService,
    private toaster: HxToastrService,
    private tr: TranslocoService
  ) {}

  ngOnInit() {
    this.coinBrandId = this.brandId;
    this.coinCountryId = this.countryId;
    void this.load();
  }

  async load() {
    this.loading = true;
    this.error = false;
    try {
      const [client, countries] = await Promise.all([
        this.clients.getClient(this.id), firstValueFrom(this.countriesService.getMyCountries())
      ]);
      this.countries = countries;
      this.client = {...client, client: {...client.client}};
      this.hasStoredPhone = !!client.client.phone;
      this.originalBirthDate = client.client.birthDate;
      const country = countries.find(item => item.id === (client.client.countryId ?? this.countryId));
      this.contacts = (client.additionalContacts || []).map(contact => ({...contact,
        value: contact.type === 'PHONE' ? this.phoneInputValue(contact.value, country) : contact.value}));
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
    }
  }

  async changeTab(tab: 'profile' | 'brand' | 'coins' | 'comments') {
    this.tab = tab;
    if (tab === 'profile') {
      this.sectionRequestId++;
      this.sectionLoading = false;
      return;
    }
    if (tab === 'brand') {await this.loadProperties();}
    if (tab === 'coins') {await this.loadCoins();}
    if (tab === 'comments') {await this.loadComments();}
  }

  addContact(type: 'PHONE' | 'EMAIL') {
    this.contacts.push({type, value: ''} as ContactModel);
  }

  removeContact(index: number) { this.contacts.splice(index, 1); }

  get birthDateChanged(): boolean { return (this.client?.client.birthDate || '') !== (this.originalBirthDate || ''); }

  get selectedCountry(): CountryModel | undefined {
    return this.countries.find(country => country.id === (this.client?.client.countryId ?? this.countryId));
  }

  get selectedCoinCountry(): CountryModel | undefined {
    return this.countries.find(country => country.id === this.coinCountryId);
  }

  get selectedCoinBrand(): BrandModel | undefined {
    return this.brands.find(brand => brand.id === this.coinBrandId);
  }

  get calendarTitle(): string {
    return `${this.tr.translate('hx.client-list.months.' + this.calendarMonth)} ${this.calendarYear}`;
  }

  get calendarMaxYear(): number {return new Date().getFullYear();}

  get calendarWeekdays(): string[] {
    const locales: Record<string, string> = {kk: 'kk-KZ', en: 'en-US', ru: 'ru-RU'};
    const locale = locales[this.tr.getActiveLang()] || 'ru-RU';
    return Array.from({length: 7}, (_, index) => new Intl.DateTimeFormat(locale, {weekday: 'short'})
      .format(new Date(2024, 0, 1 + index)));
  }

  get calendarDays(): {day: number; date: string; disabled: boolean}[] {
    const offset = (new Date(this.calendarYear, this.calendarMonth - 1, 1).getDay() + 6) % 7;
    const length = new Date(this.calendarYear, this.calendarMonth, 0).getDate();
    const today = new Date();
    return Array.from({length: offset + length}, (_, index) => {
      const day = index - offset + 1;
      const date = `${this.calendarYear}-${String(this.calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      return {day: day > 0 ? day : 0, date, disabled: day > 0 && new Date(this.calendarYear, this.calendarMonth - 1, day) > today};
    });
  }

  toggleCalendar() {
    if (!this.calendarOpen && this.client) {
      const date = this.client.client.birthDate;
      this.calendarYear = date ? Number(date.slice(0, 4)) : 2000;
      this.calendarMonth = date ? Number(date.slice(5, 7)) : 1;
    }
    this.calendarOpen = !this.calendarOpen;
  }

  shiftCalendarMonth(amount: number) {
    const date = new Date(this.calendarYear, this.calendarMonth - 1 + amount, 1);
    if (date.getFullYear() < 1900 || date.getFullYear() > new Date().getFullYear()) {return;}
    this.calendarYear = date.getFullYear();
    this.calendarMonth = date.getMonth() + 1;
  }

  setCalendarYear(value: number) {
    if (Number.isInteger(value) && value >= 1900 && value <= this.calendarMaxYear) {
      this.calendarYear = value;
    }
  }

  selectBirthDate(date?: string) {
    if (this.client) {this.client.client.birthDate = date;}
    this.calendarOpen = false;
  }

  private phoneInputValue(value: string, country?: CountryModel): string {
    const digits = (value || '').replace(/\D/g, '');
    return country ? digits.slice(-country.phoneLength) : digits;
  }

  private fullPhone(value: string, country?: CountryModel): string {
    const digits = (value || '').replace(/\D/g, '');
    if (!country || !digits) {return digits;}
    const prefix = country.phonePrefix.replace(/\D/g, '');
    return digits.length === country.phoneLength ? prefix + digits : digits;
  }

  private isCompletePhone(value: string, country?: CountryModel): boolean {
    if (!country) {return false;}
    const digits = value.replace(/\D/g, '');
    const prefix = country.phonePrefix.replace(/\D/g, '');
    return digits.length === country.phoneLength ||
      (digits.length === country.phoneLength + prefix.length && digits.startsWith(prefix));
  }

  isValidContact(contact: ContactModel): boolean {
    const value = (contact.value || '').trim();
    if (!value) {return true;}
    if (contact.type === 'EMAIL') {return value.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);}
    return this.isCompletePhone(value, this.selectedCountry);
  }

  async save() {
    if (!this.client || this.saving) {return;}
    if (this.birthDateChanged && this.originalBirthDate && !this.comment.trim()) {
      this.toaster.error(this.tr.translate('hx.client-edit.ts.commentEmpty'));
      return;
    }
    if (this.contacts.some(contact => !this.isValidContact(contact))) {
      this.toaster.error(this.tr.translate('hx.client-profile.invalidContact'));
      return;
    }
    if (!this.hasStoredPhone && this.client.client.phone &&
        !this.isCompletePhone(this.client.client.phone, this.selectedCountry)) {
      this.toaster.error(this.tr.translate('hx.client-profile.invalidPhone'));
      return;
    }
    this.saving = true;
    try {
      const request = this.clients.buildRequest({
        client: this.client.client,
        additionalContacts: this.contacts.filter(contact => contact.value?.trim()).map(contact => ({...contact,
          value: contact.type === 'PHONE' ? this.fullPhone(contact.value, this.selectedCountry) : contact.value.trim()})),
        additionalAddresses: this.client.additionalAddresses || []
      });
      request.phone = this.fullPhone(request.phone || '', this.selectedCountry);
      request.birthDate = request.birthDate || undefined;
      request.countryId = request.countryId ?? this.countryId;
      request.comment = this.comment.trim();
      request.isBirthDateUpdated = this.birthDateChanged;
      await this.clients.saveClient(request);
      this.toaster.success(this.tr.translate('hx.client-edit.ts.dataUpdated'));
      this.activeModal.close(true);
    } catch {
      this.toaster.error(this.tr.translate('hx.client-profile.saveError'));
    } finally {
      this.saving = false;
    }
  }

  async loadProperties() {
    const requestId = ++this.sectionRequestId;
    this.sectionLoading = true;
    try {
      const [properties, brands] = await Promise.all([
        this.clients.getClientBrandProperties(this.id), this.brandsService.getMyBrands()
      ]);
      if (requestId === this.sectionRequestId) {
        this.properties = properties;
        this.brands = brands;
      }
    } catch { if (requestId === this.sectionRequestId) { this.toaster.error(this.tr.translate('hx.client-list.loadError')); } }
    finally { if (requestId === this.sectionRequestId) { this.sectionLoading = false; } }
  }

  editProperty(property?: ClientPropertyModel) {
    this.propertyDraft = {
      id: property?.id,
      brandId: property?.brandId ?? this.brandId,
      vip: property?.vip ?? false,
      blacklisted: property?.blacklisted ?? false,
      note: property?.note ?? ''
    };
  }

  get draftBrandTitle(): string {
    return this.brands.find(brand => brand.id === this.propertyDraft?.brandId)?.title || '';
  }

  async saveProperty() {
    if (!this.propertyDraft || this.saving) {return;}
    this.saving = true;
    try {
      await this.clients.saveClientBrandProperty(this.id, this.propertyDraft);
      this.propertyDraft = undefined;
      await this.loadProperties();
      this.toaster.success(this.tr.translate('hx.client-edit.ts.dataUpdated'));
    } catch { this.toaster.error(this.tr.translate('hx.client-list.loadError')); }
    finally { this.saving = false; }
  }

  async removeProperty(property: ClientPropertyModel) {
    this.sectionLoading = true;
    try {
      await this.clients.removeClientBrandProperty(property.id);
      await this.loadProperties();
    } catch { this.toaster.error(this.tr.translate('hx.client-list.loadError')); }
    finally { this.sectionLoading = false; }
  }

  async loadCoins(seek?: string, direction?: SeekDirection) {
    const requestId = ++this.sectionRequestId;
    this.sectionLoading = true;
    try {
      const brandId = this.coinBrandId;
      const countryId = this.coinCountryId;
      const [amount, result, brands] = await Promise.all([
        this.coinService.getActiveCoins({clientId: this.id, brandId, countryId}),
        this.coinService.getCoinTransaction({clientId: this.id, brandId, countryId, limit: 20, seek, seekDirection: direction}),
        this.brands.length ? Promise.resolve(this.brands) : this.brandsService.getMyBrands()
      ]);
      if (requestId === this.sectionRequestId) {
        this.brands = brands;
        this.activeCoins = amount;
        this.coins = result.list || [];
        this.coinSeek = {first: result.first, last: result.last};
        this.coinPage = direction === SeekDirection.next ? this.coinPage + 1 : direction === SeekDirection.prev ? this.coinPage - 1 : 1;
        this.coinHasNext = direction === SeekDirection.prev || !!result.hasNext;
      }
    } catch { if (requestId === this.sectionRequestId) { this.toaster.error(this.tr.translate('hx.client-list.loadError')); } }
    finally { if (requestId === this.sectionRequestId) { this.sectionLoading = false; } }
  }

  selectCoinBrand(id: number) {this.coinBrandId = id; return this.loadCoins();}
  selectCoinCountry(id: number) {this.coinCountryId = id; return this.loadCoins();}
  nextCoins(): Promise<void> {
    return this.coinHasNext && this.coinSeek?.last ? this.loadCoins(this.coinSeek.last, SeekDirection.next) : Promise.resolve();
  }
  previousCoins(): Promise<void> {
    return this.coinPage > 1 && this.coinSeek?.first ? this.loadCoins(this.coinSeek.first, SeekDirection.prev) : Promise.resolve();
  }

  async loadComments() {
    const requestId = ++this.sectionRequestId;
    this.sectionLoading = true;
    try {
      const comments = await this.clients.getClientComments(this.id);
      if (requestId === this.sectionRequestId) { this.comments = comments; }
    }
    catch { if (requestId === this.sectionRequestId) { this.toaster.error(this.tr.translate('hx.client-list.loadError')); } }
    finally { if (requestId === this.sectionRequestId) { this.sectionLoading = false; } }
  }
}
